import { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  Text,
  TextInput,
  ActionIcon,
  CloseButton,
  Menu,
  Avatar,
  Divider,
  Badge,
  Box,
  Kbd,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { SkipLink } from '@/components/SkipLink';
import { RouteAnnouncer } from '@/components/RouteAnnouncer';
import {
  IconDashboard,
  IconPackage,
  IconShoppingCart,
  IconTruck,
  IconReportMoney,
  IconSettings,
  IconShield,
  IconChartBar,
  IconUsers,
  IconLogout,
  IconSearch,
  IconBell,
  IconSun,
  IconMoon,
  IconBoxSeam,
  IconArrowsExchange,
  IconClipboardCheck,
  IconBuildingWarehouse,
  IconFileInvoice,
  IconReceipt,
  IconUserCircle,
  IconClock,
  IconAlertTriangle,
  IconCertificate,
  IconLayoutDashboard,
  IconChevronRight,
  IconChevronLeft,
  IconAddressBook,
  IconActivity,
  IconCurrencyDollar,
  IconTrendingUp,
  IconCreditCard,
  IconFileDescription,
  IconBuildingBank,
  IconWorld,
  IconThermometer,
  IconPalette,
  IconCalendarTime,
  IconLeaf,
  IconFlask,
  IconLock,
  IconFileCheck,
  IconTestPipe,
  IconSalad,
  IconListCheck,
  IconRefresh,
  IconTruckLoading,
  IconPackageImport,
  IconTemperature,
  IconRoute,
  IconCalendarEvent,
  IconCash,
  IconCalculator,
  IconScan,
  IconChartLine,
  IconTruckDelivery,
  IconTool,
  IconLicense,
  IconGps,
  IconGitBranch,
  IconTemplate,
  IconPlayerPlay,
  IconTransfer,
  IconRobot,
  IconCpu,
  IconBriefcase,
  IconPercentage,
  IconReceiptRefund,
  IconShieldCheck,
  IconShieldLock,
  IconDatabase,
  IconFingerprint,
  IconAlertCircle,
  IconChecks,
  IconFileAnalytics,
  IconReportAnalytics,
  IconHome,
  IconFileDollar,
  IconFolders,
  IconPlus,
} from '@tabler/icons-react';
import { useAuthStore } from '@/store/auth';
import SearchSpotlight from '@/components/SearchSpotlight';
import NextTaskSuggestion from '@/components/NextTaskSuggestion';
import { useTaskAnticipation } from '@/hooks/useTaskAnticipation';

// ---------------------------------------------------------------------------
// Navigation configuration with sections
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  icon: typeof IconDashboard;
  path?: string;
  requiredPermission?: string;
  children?: { label: string; path: string }[];
  subsection?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  // -----------------------------------------------------------------------
  // 1. MY WORKSPACE — shown to everyone, role-adaptive
  // -----------------------------------------------------------------------
  {
    title: 'MY WORKSPACE',
    items: [
      {
        label: 'Dashboard',
        icon: IconDashboard,
        path: '/dashboard',
        subsection: 'Overview',
      },
      {
        label: 'Daily Summary',
        icon: IconLayoutDashboard,
        path: '/operations/daily-summary',
        subsection: 'Overview',
      },
      {
        label: 'Financial Dashboard',
        icon: IconChartLine,
        path: '/reports/financial-dashboard',
        subsection: 'Overview',
        requiredPermission: 'finance.view',
      },
      {
        label: 'AI Tools',
        icon: IconRobot,
        subsection: 'Intelligence',
        children: [
          { label: 'My Day', path: '/ai/my-day' },
          { label: 'AI Copilot', path: '/ai/copilot' },
          { label: 'AI Order Chat', path: '/ai/order-chat' },
          { label: 'Document Intelligence', path: '/ai/documents' },
          { label: 'AI Email Parsing', path: '/ai/emails' },
          { label: 'AI Quality', path: '/ai/quality' },
          { label: 'ML Forecasting', path: '/ai/ml-forecasting' },
          { label: 'Demand Intelligence', path: '/ai/demand' },
          { label: 'Market Intelligence', path: '/ai/market' },
          { label: 'Voice Agent', path: '/ai/voice-agent' },
          { label: 'Voice Agent Live', path: '/ai/voice-agent-live' },
          { label: 'Voice Prompts', path: '/ai/voice-prompts' },
          { label: 'Financial Intelligence', path: '/ai/financial' },
          { label: 'Vendor Cost Intel', path: '/ai/vendor-costs' },
          { label: 'Profit Optimization', path: '/ai/profit' },
          { label: 'Talent Matching', path: '/ai/talent-matching' },
          { label: 'Retention Risk', path: '/ai/retention-risk' },
          { label: 'Workforce Planning', path: '/ai/workforce-planning' },
          { label: 'Lead Scoring', path: '/ai/lead-scoring' },
          { label: 'Churn Prevention', path: '/ai/churn-prevention' },
          { label: 'Sales Forecasting', path: '/ai/sales-forecasting' },
          { label: 'Pick Optimization', path: '/ai/pick-optimization' },
          { label: 'Inventory Replenishment', path: '/ai/inventory-replenishment' },
          { label: 'Slotting Advisor', path: '/ai/slotting-advisor' },
          { label: 'Contract Analyzer', path: '/ai/contract-analyzer' },
          { label: 'Contract Negotiation', path: '/ai/contract-negotiation' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 2. SALES & CUSTOMERS — sales reps see this first
  // -----------------------------------------------------------------------
  {
    title: 'SALES & CUSTOMERS',
    items: [
      {
        label: 'Sales',
        icon: IconShoppingCart,
        subsection: 'Orders & A/R',
        children: [
          { label: 'Dashboard', path: '/sales/dashboard' },
          { label: 'Orders', path: '/sales/orders' },
          { label: 'New Order', path: '/sales/new-order' },
          { label: 'Customer Portal', path: '/sales/customer-portal' },
          { label: 'Customers', path: '/sales/customers' },
          { label: 'Credit Memos', path: '/sales/credit-memos' },
          { label: 'Payments', path: '/sales/payments' },
          { label: 'Cash Receipts', path: '/sales/cash-receipts' },
          { label: 'Field Visits', path: '/sales/field-visits' },
          { label: 'Returns (RMA)', path: '/sales/returns' },
          { label: 'Warranty', path: '/sales/warranty' },
        ],
      },
      {
        label: 'CRM',
        icon: IconAddressBook,
        subsection: 'Orders & A/R',
        children: [
          { label: 'Pipeline', path: '/crm/pipeline' },
          { label: 'Contacts', path: '/crm/contacts' },
          { label: 'Activities', path: '/crm/activities' },
        ],
      },
      {
        label: 'Commerce',
        icon: IconCreditCard,
        subsection: 'Channels',
        children: [
          { label: 'E-Commerce', path: '/commerce/ecommerce' },
          { label: 'Broker Commission', path: '/commerce/broker-commission' },
          { label: 'Loyalty & Rebates', path: '/commerce/loyalty' },
          { label: 'RFQ', path: '/commerce/rfq' },
          { label: 'Recurring Orders', path: '/commerce/recurring' },
          { label: 'POS', path: '/commerce/pos' },
          { label: 'Substitutions', path: '/commerce/substitutions' },
          { label: 'Price Locks', path: '/commerce/price-locks' },
          { label: 'Predictive Pricing', path: '/commerce/predictive-pricing' },
          { label: 'Menu Planning', path: '/commerce/menu-planning' },
          { label: 'Customer Preferences', path: '/commerce/customer-preferences' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 3. PURCHASING & VENDORS
  // -----------------------------------------------------------------------
  {
    title: 'PURCHASING & VENDORS',
    items: [
      {
        label: 'Purchasing',
        icon: IconTruck,
        subsection: 'Orders & A/P',
        children: [
          { label: 'Purchase Orders', path: '/purchasing/purchase-orders' },
          { label: 'Vendor Portal', path: '/purchasing/vendor-portal' },
          { label: 'Vendors', path: '/purchasing/vendors' },
          { label: 'Vendor Catalog', path: '/purchasing/vendor-catalog' },
          { label: 'Landed Costs', path: '/purchasing/landed-costs' },
          { label: 'Requisitions', path: '/purchasing/requisitions' },
        ],
      },
      {
        label: 'AP Automation',
        icon: IconScan,
        subsection: 'Orders & A/P',
        children: [
          { label: 'AP Dashboard', path: '/ap' },
          { label: 'New Invoice', path: '/ap/new-invoice' },
          { label: 'Check Run', path: '/ap/check-run' },
          { label: 'Invoice Capture', path: '/ap/invoice-capture' },
          { label: 'Corporate Cards', path: '/ap/corporate-cards' },
          { label: 'Cash Forecast', path: '/ap/cash-forecast' },
          { label: 'Exception Queue', path: '/ap/exception-queue' },
        ],
      },
      {
        label: 'Pricing',
        icon: IconCurrencyDollar,
        subsection: 'Pricing',
        requiredPermission: 'pricing.view',
        children: [
          { label: 'Price Management', path: '/pricing' },
          { label: 'Price Sheets', path: '/pricing/price-sheets' },
          { label: 'Dynamic Discounts', path: '/pricing/dynamic-discounts' },
          { label: 'Rebates', path: '/pricing/rebates' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 4. WAREHOUSE & INVENTORY
  // -----------------------------------------------------------------------
  {
    title: 'WAREHOUSE & INVENTORY',
    items: [
      {
        label: 'Inventory',
        icon: IconPackage,
        subsection: 'Inventory',
        children: [
          { label: 'Products', path: '/inventory/products' },
          { label: 'Stock Levels', path: '/inventory/stock-levels' },
          { label: 'Receiving', path: '/inventory/receiving' },
          { label: 'Physical Count', path: '/inventory/physical-count' },
          { label: 'Pallets', path: '/inventory/pallets' },
          { label: 'Expiration', path: '/inventory/expiration' },
          { label: 'Spoilage Risk', path: '/inventory/spoilage-risk' },
          { label: 'Shrink Analytics', path: '/inventory/shrink-analytics' },
        ],
      },
      {
        label: 'WMS',
        icon: IconBuildingWarehouse,
        subsection: 'Operations',
        children: [
          { label: 'Pick Lists', path: '/operations/pick-lists' },
          { label: 'Shipping', path: '/operations/shipping' },
          { label: 'Cycle Counts', path: '/operations/cycle-counts' },
          { label: 'Receiving Dock', path: '/operations/receiving' },
          { label: 'Cold Chain Monitor', path: '/operations/cold-chain-monitor' },
          { label: 'Route Optimization', path: '/operations/route-optimization' },
          { label: 'Scheduling', path: '/operations/scheduling' },
        ],
      },
      {
        label: 'Warehouse Ops',
        icon: IconBoxSeam,
        subsection: 'Operations',
        children: [
          { label: 'WMS Zones', path: '/warehouse/wms' },
          { label: 'Advanced WMS', path: '/warehouse/advanced' },
          { label: 'Dock Scheduling', path: '/warehouse/dock-scheduling' },
          { label: 'Yard Management', path: '/warehouse/yard' },
          { label: 'Slotting', path: '/warehouse/slotting' },
          { label: 'Allocation', path: '/warehouse/allocation' },
          { label: 'MRP', path: '/warehouse/mrp' },
          { label: 'Pack House', path: '/warehouse/pack-house' },
          { label: 'Pallet Build', path: '/warehouse/pallet-build' },
          { label: 'Shelf Life', path: '/warehouse/shelf-life' },
        ],
      },
      {
        label: 'IoT & Sensors',
        icon: IconCpu,
        subsection: 'Operations',
        children: [
          { label: 'Sensor Dashboard', path: '/iot/sensors' },
        ],
      },
      {
        label: 'Supply Chain',
        icon: IconArrowsExchange,
        subsection: 'Supply Chain',
        children: [
          { label: 'Grading', path: '/supply-chain/grading' },
          { label: 'COOL', path: '/supply-chain/cool' },
          { label: 'Ethylene & CA', path: '/supply-chain/ethylene-ca' },
          { label: 'Freshness', path: '/supply-chain/freshness' },
          { label: 'Trace', path: '/supply-chain/trace' },
          { label: 'Mock Recall', path: '/supply-chain/mock-recall' },
        ],
      },
      {
        label: 'Maintenance',
        icon: IconTool,
        subsection: 'Supply Chain',
        children: [
          { label: 'Equipment & Work Orders', path: '/maintenance/equipment' },
        ],
      },
      {
        label: 'Manufacturing',
        icon: IconSettings,
        subsection: 'Supply Chain',
        children: [
          { label: 'Work Orders', path: '/manufacturing/orders' },
          { label: 'Bill of Materials', path: '/manufacturing/boms' },
          { label: 'Work Centers', path: '/manufacturing/work-centers' },
          { label: 'Production Tracking', path: '/manufacturing/tracking' },
          { label: 'Change Orders (ECO)', path: '/manufacturing/ecos' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 5. ACCOUNTING & FINANCE
  // -----------------------------------------------------------------------
  {
    title: 'ACCOUNTING & FINANCE',
    items: [
      {
        label: 'Accounting',
        icon: IconReportMoney,
        subsection: 'General Ledger',
        children: [
          { label: 'General Ledger', path: '/accounting/gl-entries' },
          { label: 'Journal Entries', path: '/accounting/journal-entries' },
          { label: 'New Journal Entry', path: '/accounting/journal-entries/new' },
          { label: 'Trial Balance', path: '/accounting/trial-balance' },
          { label: 'Banking', path: '/accounting/banking' },
          { label: 'Bank Deposits', path: '/accounting/bank-deposits' },
          { label: 'Bank Import', path: '/accounting/bank-import' },
          { label: 'Bank Reconciliation', path: '/accounting/bank-reconciliation' },
          { label: 'Fixed Assets', path: '/accounting/fixed-assets' },
          { label: 'Investments', path: '/accounting/investments' },
          { label: 'Credit Management', path: '/accounting/credit-management' },
          { label: 'Recurring Entries', path: '/accounting/recurring-entries' },
          { label: 'Budgets', path: '/accounting/budgets' },
          { label: 'Currency Rates', path: '/accounting/currency' },
          { label: 'Fiscal Periods', path: '/accounting/fiscal-periods' },
          { label: 'Sub-Ledger Dashboard', path: '/accounting/sub-ledger-dashboard' },
          { label: 'Fixed Asset Recon', path: '/accounting/fixed-asset-recon' },
          { label: 'Check Setup', path: '/accounting/check-setup' },
          { label: 'Multi-Book', path: '/accounting/multi-book' },
        ],
      },
      {
        label: 'Treasury',
        icon: IconBuildingBank,
        subsection: 'Cash & Payments',
        children: [
          { label: 'Petty Cash', path: '/finance/petty-cash' },
          { label: 'Cash Application', path: '/finance/cash-application' },
          { label: 'Bank Import', path: '/finance/bank-import' },
          { label: 'Plaid Feeds', path: '/finance/plaid' },
          { label: 'Check & Wire', path: '/finance/check-wire' },
          { label: 'Payment Links', path: '/finance/payment-links' },
          { label: 'Payment Orchestrator', path: '/finance/payment-orchestrator' },
          { label: 'Payment Terms', path: '/finance/payment-terms' },
          { label: 'Chargebacks', path: '/finance/chargebacks' },
        ],
      },
      {
        label: 'Financial Ops',
        icon: IconFileDollar,
        subsection: 'Planning & Close',
        children: [
          { label: 'Intercompany', path: '/finance/intercompany' },
          { label: 'Financial Close', path: '/finance/financial-close' },
          { label: 'Period Close Hub', path: '/admin/period-close' },
          { label: 'Lease Accounting', path: '/finance/lease-accounting' },
          { label: 'Revenue Recognition', path: '/finance/revenue' },
          { label: 'Cost Centers', path: '/finance/cost-centers' },
          { label: 'Bad Debt', path: '/finance/bad-debt' },
          { label: 'AP Aging', path: '/finance/ap-aging' },
          { label: 'AP Duplicates', path: '/finance/ap-duplicates' },
          { label: 'Credit Scoring', path: '/finance/credit-scoring' },
          { label: 'Expense Reimburse.', path: '/finance/expenses' },
          { label: 'Credit Card Recon', path: '/finance/credit-card-recon' },
          { label: 'Vendor Stmt. Recon', path: '/finance/vendor-statement-recon' },
          { label: 'Project Accounting', path: '/finance/project-accounting' },
        ],
      },
      { label: 'Forecasting', icon: IconTrendingUp, path: '/forecasting', subsection: 'Planning & Close' },
    ],
  },
  // -----------------------------------------------------------------------
  // 6. FINANCIAL REPORTS
  // -----------------------------------------------------------------------
  {
    title: 'FINANCIAL REPORTS',
    items: [
      {
        label: 'Financial Statements',
        icon: IconFileAnalytics,
        subsection: 'Statements',
        children: [
          { label: 'Income Statement (P&L)', path: '/accounting/reports?type=income_statement' },
          { label: 'Balance Sheet', path: '/accounting/reports?type=balance_sheet' },
          { label: 'Cash Flow Statement', path: '/accounting/reports?type=cash_flow' },
          { label: 'Reports Builder', path: '/accounting/reports' },
          { label: 'Financial Dashboard', path: '/reports/financial-dashboard' },
          { label: 'CPA Audit Package', path: '/accounting/cpa-audit-package' },
          { label: 'Audit Readiness', path: '/accounting/audit-readiness' },
        ],
      },
      {
        label: 'Reports',
        icon: IconChartBar,
        subsection: 'Analytics',
        children: [
          { label: 'Dashboards', path: '/reports/dashboards' },
          { label: 'BI Dashboard', path: '/reports/bi' },
          { label: 'Report Builder', path: '/reports/builder' },
          { label: 'Async Reports', path: '/reports/async' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 7. PAYROLL & HR
  // -----------------------------------------------------------------------
  {
    title: 'PAYROLL & HR',
    items: [
      {
        label: 'Payroll',
        icon: IconCash,
        subsection: 'Payroll Runs',
        children: [
          { label: 'Payroll Dashboard', path: '/payroll' },
          { label: 'Payroll Runs', path: '/payroll/runs' },
          { label: 'New Payroll Run', path: '/payroll/new-run' },
          { label: 'Payroll Setup', path: '/payroll/setup' },
          { label: 'Pay Stubs', path: '/payroll/pay-stubs' },
          { label: 'Advanced Payroll', path: '/payroll/advanced' },
          { label: 'Payroll ACH', path: '/payroll/ach' },
          { label: 'Payroll Register', path: '/payroll/register' },
          { label: 'Garnishments', path: '/payroll/garnishments' },
          { label: 'Retro Pay', path: '/payroll/retro-pay' },
          { label: 'Tips', path: '/payroll/tips' },
          { label: 'International', path: '/payroll/international' },
          { label: 'State Wages', path: '/payroll/state-wages' },
          { label: 'Certified Payroll', path: '/payroll/certified' },
          { label: 'Earned Wage Access', path: '/payroll/ewa' },
          { label: 'Automation', path: '/payroll/automation' },
          { label: 'AI Approval', path: '/payroll/ai-approval' },
        ],
      },
      {
        label: 'Time & Attendance',
        icon: IconClock,
        subsection: 'Time Tracking',
        children: [
          { label: 'Time Clock', path: '/payroll/time-attendance' },
          { label: 'Timesheets', path: '/ta/timesheets' },
          { label: 'PTO Management', path: '/ta/pto' },
          { label: 'Schedule Calendar', path: '/ta/schedule' },
          { label: 'Attendance Reports', path: '/ta/reports' },
          { label: 'Devices (GT8)', path: '/ta/devices' },
          { label: 'Geofence Zones', path: '/ta/geofence' },
          { label: 'Shift Swaps', path: '/ta/shift-swaps' },
          { label: 'PTO Policies', path: '/ta/pto-policies' },
          { label: 'Team Calendar', path: '/ta/team-calendar' },
          { label: 'AI Intelligence', path: '/ta/intelligence' },
          { label: 'Kiosk Terminals', path: '/kiosk/devices' },
        ],
      },
      {
        label: 'Payroll Recon',
        icon: IconReportAnalytics,
        subsection: 'Reconciliation',
        children: [
          { label: 'Tax Reconciliation', path: '/payroll/tax-reconciliation' },
          { label: 'Benefits Reconciliation', path: '/payroll/benefits-reconciliation' },
          { label: 'GL Reconciliation', path: '/payroll/gl-reconciliation' },
          { label: 'Benefits Recon', path: '/payroll/benefits-vendor-recon' },
          { label: 'Bank Recon', path: '/payroll/bank-recon' },
        ],
      },
      {
        label: 'Employees',
        icon: IconUsers,
        subsection: 'People',
        children: [
          { label: 'Employees', path: '/operations/employees' },
          { label: 'SLA Tracking', path: '/operations/sla-tracking' },
          { label: 'Change Management', path: '/operations/change-management' },
        ],
      },
      {
        label: 'HR & Recruiting',
        icon: IconBriefcase,
        subsection: 'People',
        children: [
          { label: 'Employee Self-Service', path: '/hr/employee-self-service' },
          { label: 'ESS Admin', path: '/hr/ess-admin' },
          { label: 'Recruiting', path: '/hr/recruiting' },
          { label: 'ACA Compliance', path: '/hr/aca' },
          { label: '401(k)', path: '/hr/401k' },
          { label: 'ERISA', path: '/hr/erisa' },
          { label: 'Leave & Benefits', path: '/hr/leave-benefits' },
          { label: 'Onboarding', path: '/hr/onboarding' },
          { label: 'LMS', path: '/hr/lms' },
        ],
      },
      {
        label: 'Service Desk',
        icon: IconUserCircle,
        subsection: 'Support',
        children: [
          { label: 'Support Tickets', path: '/service/tickets' },
          { label: 'Agent Desktop', path: '/service/agent-desktop' },
          { label: 'Knowledge Base', path: '/service/knowledge-base' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 8. FLEET & LOGISTICS — keep existing items
  // -----------------------------------------------------------------------
  {
    title: 'FLEET & LOGISTICS',
    items: [
      {
        label: 'Fleet',
        icon: IconTruckDelivery,
        subsection: 'Fleet',
        children: [
          { label: 'Fleet Dashboard', path: '/fleet' },
          { label: 'Maintenance', path: '/fleet/maintenance' },
          { label: 'DOT Compliance', path: '/fleet/dot-compliance' },
          { label: 'Asset Tracking', path: '/fleet/asset-tracking' },
          { label: 'Driver Safety', path: '/fleet/driver-safety' },
          { label: 'Fleet TMS', path: '/fleet/tms' },
          { label: 'Driver Performance', path: '/fleet/driver-performance' },
          { label: 'Drug/Alcohol Testing', path: '/fleet/dot-testing' },
          { label: 'Dashcam', path: '/fleet/dashcam' },
        ],
      },
      {
        label: 'TMS',
        icon: IconRoute,
        subsection: 'Transport',
        children: [
          { label: 'TMS Advanced', path: '/tms/advanced' },
          { label: 'TMS Intelligence', path: '/tms/intelligence' },
          { label: 'Rate Shopping', path: '/tms/rate-shopping' },
          { label: 'Freight Audit', path: '/tms/freight-audit' },
          { label: 'Load Planning', path: '/tms/load-planning' },
          { label: 'Route Accounting', path: '/tms/route-accounting' },
        ],
      },
      {
        label: 'Logistics',
        icon: IconWorld,
        subsection: 'Transport',
        children: [
          { label: '3PL Management', path: '/logistics/3pl' },
          { label: 'ML Optimization', path: '/logistics/ml' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 9. COMPLIANCE & FOOD SAFETY — keep existing items
  // -----------------------------------------------------------------------
  {
    title: 'COMPLIANCE & FOOD SAFETY',
    items: [
      {
        label: 'Safety & Quality',
        icon: IconShield,
        subsection: 'Food Safety',
        children: [
          { label: 'Food Safety', path: '/compliance/food-safety' },
          { label: 'HACCP Plans', path: '/compliance/haccp' },
          { label: 'GAP/GMP', path: '/compliance/gap-gmp' },
          { label: 'GFSI Certification', path: '/compliance/gfsi' },
          { label: 'Allergen Control', path: '/compliance/allergen' },
          { label: 'Env. Monitoring', path: '/compliance/env-monitoring' },
          { label: 'Food Defense', path: '/compliance/food-defense' },
          { label: 'Recall Management', path: '/compliance/recalls' },
        ],
      },
      {
        label: 'Regulatory',
        icon: IconCertificate,
        subsection: 'Food Safety',
        children: [
          { label: 'FSMA Controls', path: '/compliance/fsma' },
          { label: 'PACA Trust', path: '/compliance/paca' },
          { label: 'Cold Chain', path: '/compliance/cold-chain' },
          { label: 'Organic COC', path: '/compliance/organic' },
          { label: 'Pesticide & MRL', path: '/compliance/pesticide-mrl' },
          { label: 'LGMA', path: '/compliance/lgma' },
          { label: 'Organic Enforcement', path: '/regulatory/organic-enforcement' },
          { label: 'PCI Compliance', path: '/regulatory/pci' },
          { label: 'Phytosanitary', path: '/regulatory/phytosanitary' },
          { label: 'Privacy', path: '/regulatory/privacy' },
          { label: 'State Organic', path: '/regulatory/state-organic' },
          { label: 'USERRA', path: '/regulatory/userra' },
          { label: 'WARN Act', path: '/regulatory/warn-act' },
          { label: 'Weights & Measures', path: '/regulatory/weights-measures' },
        ],
      },
      {
        label: 'Audit & Security',
        icon: IconLock,
        subsection: 'Audit',
        children: [
          { label: 'Auditor Portal', path: '/audit/auditor-portal' },
          { label: 'Audit Integrity', path: '/audit/integrity' },
          { label: 'Compliance Evidence', path: '/audit/compliance-evidence' },
          { label: 'Compliance Scheduler', path: '/compliance/scheduler' },
        ],
      },
      {
        label: 'Extended Compliance',
        icon: IconClipboardCheck,
        subsection: 'Extended',
        children: [
          { label: 'Permits', path: '/compliance/permits' },
          { label: 'Bioterrorism', path: '/compliance/bioterrorism' },
          { label: 'BCP', path: '/compliance/bcp' },
          { label: 'CFATS', path: '/compliance/cfats' },
          { label: 'Carbon & ESG', path: '/compliance/carbon-esg' },
          { label: 'Commodity Assess.', path: '/compliance/commodity-assessments' },
          { label: 'Data Breach', path: '/compliance/data-breach' },
          { label: 'FCRA', path: '/compliance/fcra' },
          { label: 'FDA FURLS', path: '/compliance/fda-furls' },
          { label: 'FDA Registration', path: '/compliance/fda-registration' },
          { label: 'FSMA Transport', path: '/compliance/fsma-transport' },
          { label: 'Import Duty', path: '/compliance/import-duty' },
          { label: 'Marketing Compliance', path: '/compliance/marketing' },
          { label: 'OFAC Sanctions', path: '/compliance/ofac' },
        ],
      },
      {
        label: 'State Agencies',
        icon: IconCertificate,
        subsection: 'Extended',
        children: [
          { label: 'VDACS', path: '/va-tax/vdacs' },
          { label: 'VDH Health', path: '/va-tax/vdh' },
        ],
      },
      {
        label: 'Virginia Tax',
        icon: IconCalculator,
        subsection: 'State Tax',
        children: [
          { label: 'VA Compliance', path: '/va-tax' },
          { label: 'ST-1 Sales Tax', path: '/va-tax/st1' },
          { label: 'VA-4 Certificate', path: '/va-tax/va4' },
          { label: 'VA-5 Filing', path: '/va-tax/va5' },
          { label: 'VA-15 Deposit', path: '/va-tax/va15' },
          { label: 'VA-16 Reconciliation', path: '/va-tax/va16' },
          { label: 'VA Withholding', path: '/va-tax/withholding' },
          { label: 'Corporate Tax', path: '/va-tax/corporate' },
          { label: 'IFTA', path: '/va-tax/ifta' },
          { label: 'Filing Frequency', path: '/va-tax/filing-frequency' },
          { label: 'Penalty & Interest', path: '/va-tax/penalty-interest' },
          { label: 'Tangible Property', path: '/va-tax/tangible-property' },
          { label: 'Tax Intelligence', path: '/va-tax/intelligence' },
        ],
      },
      {
        label: 'Tax Forms',
        icon: IconFileCheck,
        subsection: 'Federal Tax',
        children: [
          { label: 'Form 8300', path: '/finance/form-8300' },
          { label: 'Form 4562', path: '/finance/form-4562' },
          { label: 'Schedule M-1', path: '/finance/schedule-m1' },
          { label: 'Tax Compliance', path: '/accounting/tax-compliance' },
          { label: 'Consolidated Tax', path: '/finance/consolidated-tax' },
          { label: 'Deferred Tax', path: '/finance/deferred-tax' },
          { label: 'Sales Tax Recon', path: '/finance/sales-tax-recon' },
        ],
      },
      {
        label: 'Payroll Tax',
        icon: IconReceipt,
        subsection: 'Payroll Tax',
        children: [
          { label: 'Form 940', path: '/payroll/form-940' },
          { label: 'Form 941', path: '/payroll/form-941' },
          { label: 'Form 943', path: '/payroll/form-943' },
          { label: 'W-2 Management', path: '/payroll/w2' },
          { label: 'W-4 Tracking', path: '/payroll/w4' },
          { label: 'Form 1099', path: '/payroll/1099' },
          { label: 'SUTA Filing', path: '/payroll/suta' },
          { label: 'State Reciprocity', path: '/payroll/reciprocity' },
          { label: 'CSE Remittance', path: '/payroll/cse' },
          { label: 'New Hire Reports', path: '/payroll/new-hire' },
        ],
      },
    ],
  },
  // -----------------------------------------------------------------------
  // 10. DOCUMENTS & ADMIN
  // -----------------------------------------------------------------------
  {
    title: 'DOCUMENTS & ADMIN',
    items: [
      {
        label: 'Security & Audit',
        icon: IconShieldLock,
        subsection: 'Core Admin',
        children: [
          { label: 'Documents', path: '/admin/documents' },
          { label: 'System Health', path: '/admin/system-health' },
          { label: 'Audit Log', path: '/admin/audit-log' },
          { label: 'Settings', path: '/admin/settings' },
          { label: 'Encryption Keys', path: '/admin/encryption-keys' },
          { label: 'Key Rotation', path: '/admin/key-rotation' },
          { label: 'Data Classification', path: '/admin/data-classification' },
          { label: 'Pentest Evidence', path: '/admin/pentest-evidence' },
          { label: 'SIEM Integration', path: '/admin/siem' },
          { label: 'Control Testing', path: '/admin/control-testing' },
        ],
      },
      {
        label: 'User & Access',
        icon: IconUsers,
        subsection: 'Core Admin',
        children: [
          { label: 'Roles', path: '/admin/roles' },
          { label: 'Users', path: '/admin/users' },
          { label: 'SSO', path: '/admin/sso' },
          { label: 'OAuth', path: '/admin/oauth' },
          { label: 'API Tokens', path: '/admin/api-tokens' },
          { label: 'API Gateway', path: '/admin/api-gateway' },
          { label: 'Session Security', path: '/admin/session-security' },
          { label: 'SOD Administration', path: '/admin/sod' },
          { label: 'Access Review', path: '/admin/access-review' },
          { label: 'Break-Glass Access', path: '/admin/break-glass' },
          { label: 'Dual Approval', path: '/admin/dual-approval' },
        ],
      },
      {
        label: 'Admin Hub',
        icon: IconLayoutDashboard,
        path: '/admin/hub',
        subsection: 'Core Admin',
      },
      {
        label: 'System Config',
        icon: IconSettings,
        subsection: 'System',
        children: [
          { label: 'Tenants', path: '/admin/tenants' },
          { label: 'Webhooks', path: '/admin/webhooks' },
          { label: 'Notifications', path: '/admin/notification-channels' },
          { label: 'Health Checks', path: '/admin/health' },
          { label: 'Data Retention', path: '/admin/data-retention' },
          { label: 'Archival', path: '/admin/archival' },
          { label: 'Data Migration', path: '/admin/data-migration' },
          { label: 'GP Migration', path: '/admin/gp-migration' },
          { label: 'Search Config', path: '/admin/search' },
          { label: 'DocuSign', path: '/admin/docusign' },
          { label: 'Slack Webhooks', path: '/admin/slack' },
        ],
      },
      {
        label: 'Automation',
        icon: IconPlayerPlay,
        subsection: 'Automation',
        children: [
          { label: 'Automation Hub', path: '/automation/hub' },
          { label: 'Workflow Rules', path: '/automation/workflow-rules' },
          { label: 'Workflow Monitor', path: '/automation/monitor' },
          { label: 'Recurring Tasks', path: '/automation/recurring' },
        ],
      },
      {
        label: 'Workflow',
        icon: IconGitBranch,
        subsection: 'Automation',
        children: [
          { label: 'Templates', path: '/workflow/templates' },
          { label: 'Instances', path: '/workflow/instances' },
          { label: 'EDI Management', path: '/integrations/edi' },
        ],
      },
    ],
  },
];

const iconMap: Record<string, typeof IconDashboard> = {
  Products: IconBoxSeam,
  'Stock Levels': IconBuildingWarehouse,
  Receiving: IconClipboardCheck,
  Pallets: IconPalette,
  Expiration: IconCalendarTime,
  Orders: IconFileInvoice,
  Customers: IconUserCircle,
  'Credit Memos': IconFileDescription,
  Payments: IconCreditCard,
  'Cash Receipts': IconCash,
  Contacts: IconAddressBook,
  Activities: IconActivity,
  'Purchase Orders': IconReceipt,
  Vendors: IconTruck,
  'Landed Costs': IconWorld,
  'Vendor Catalog': IconBoxSeam,
  'General Ledger': IconReportMoney,
  'Journal Entries': IconFileDescription,
  'New Journal Entry': IconPlus,
  'Trial Balance': IconBuildingBank,
  'Bank Reconciliation': IconBuildingBank,
  'Bank Deposits': IconBuildingBank,
  'Fixed Assets': IconBuildingWarehouse,
  Budgets: IconChartBar,
  'Currency Rates': IconCurrencyDollar,
  'Fiscal Periods': IconCalendarTime,
  'Tax Compliance': IconFileCheck,
  Reports: IconChartBar,
  Employees: IconUsers,
  'Food Safety': IconShield,
  'PACA Trust': IconCertificate,
  'Cold Chain': IconThermometer,
  'HACCP Plans': IconClipboardCheck,
  'GAP/GMP': IconFlask,
  'GFSI Certification': IconCertificate,
  'Allergen Control': IconAlertTriangle,
  'Env. Monitoring': IconFlask,
  'Food Defense': IconLock,
  'FSMA Controls': IconFileCheck,
  'Organic COC': IconLeaf,
  'Pesticide & MRL': IconTestPipe,
  LGMA: IconSalad,
  Dashboards: IconLayoutDashboard,
  Users: IconUsers,
  Roles: IconShield,
  'Audit Log': IconClock,
  Settings: IconSettings,
  'API Tokens': IconCertificate,
  Webhooks: IconWorld,
  Tenants: IconBuildingWarehouse,
  Transfers: IconArrowsExchange,
  'Time Entries': IconClock,
  Recalls: IconAlertTriangle,
  Certifications: IconCertificate,
  'Pick Lists': IconListCheck,
  'Cycle Counts': IconRefresh,
  Shipping: IconTruckLoading,
  'Receiving Dock': IconPackageImport,
  'Cold Chain Monitor': IconTemperature,
  'Route Optimization': IconRoute,
  Scheduling: IconCalendarEvent,
  // Inventory Intelligence
  'Spoilage Risk': IconAlertTriangle,
  'Shrink Analytics': IconChartBar,
  // IoT
  'Sensor Dashboard': IconCpu,
  // Payroll
  'Payroll Dashboard': IconLayoutDashboard,
  'Payroll Runs': IconCash,
  'New Payroll Run': IconPlus,
  'Time & Attendance': IconClock,
  'Advanced Payroll': IconCalculator,
  // AP Automation
  'AP Dashboard': IconLayoutDashboard,
  'Invoice Capture': IconScan,
  'Corporate Cards': IconCreditCard,
  'Cash Forecast': IconChartLine,
  'Exception Queue': IconAlertTriangle,
  // Fleet
  'Fleet Dashboard': IconTruckDelivery,
  Maintenance: IconTool,
  'DOT Compliance': IconLicense,
  'Asset Tracking': IconGps,
  'Driver Safety': IconShieldCheck,
  // Logistics
  '3PL Management': IconBuildingWarehouse,
  'ML Optimization': IconTrendingUp,
  // Pricing (extended)
  'Price Management': IconCurrencyDollar,
  'Price Sheets': IconFileDescription,
  'Dynamic Discounts': IconPercentage,
  Rebates: IconReceiptRefund,
  // Compliance (extended)
  'Recall Management': IconAlertTriangle,
  // HR
  Recruiting: IconBriefcase,
  // Workflow & Integrations
  Templates: IconTemplate,
  Instances: IconPlayerPlay,
  'EDI Management': IconTransfer,
  // AI
  'Document Intelligence': IconRobot,
  // Audit & Security
  'Auditor Portal': IconShield,
  'Audit Integrity': IconLock,
  // Payroll (extended)
  'Tax Reconciliation': IconCalculator,
  'Benefits Reconciliation': IconReceiptRefund,
  // Admin (extended)
  'SOD Administration': IconLock,
  'Access Review': IconShieldLock,
  'Encryption Keys': IconLock,
  'Data Retention': IconDatabase,
  'Session Security': IconFingerprint,
  'Break-Glass Access': IconAlertCircle,
  'Dual Approval': IconChecks,
  'Compliance Evidence': IconFileAnalytics,
  'Pentest Evidence': IconShieldLock,
  'SIEM Integration': IconActivity,
  'Data Classification': IconDatabase,
  'Control Testing': IconChecks,
  // Commerce (new)
  'E-Commerce': IconShoppingCart,
  'Broker Commission': IconPercentage,
  'Loyalty & Rebates': IconReceiptRefund,
  RFQ: IconFileDescription,
  'Recurring Orders': IconRefresh,
  POS: IconCreditCard,
  Substitutions: IconArrowsExchange,
  'Price Locks': IconLock,
  'Predictive Pricing': IconTrendingUp,
  'Menu Planning': IconListCheck,
  'Customer Preferences': IconUsers,
  // Warehouse Ops (new)
  'WMS Zones': IconBuildingWarehouse,
  'Advanced WMS': IconBuildingWarehouse,
  'Dock Scheduling': IconCalendarEvent,
  'Yard Management': IconTruckLoading,
  Slotting: IconPackage,
  Allocation: IconArrowsExchange,
  MRP: IconChartBar,
  'Pack House': IconBoxSeam,
  'Pallet Build': IconPalette,
  'Shelf Life': IconCalendarTime,
  // Supply Chain (new)
  Grading: IconClipboardCheck,
  COOL: IconWorld,
  'Ethylene & CA': IconFlask,
  Freshness: IconLeaf,
  Trace: IconRoute,
  'Mock Recall': IconAlertTriangle,
  // Treasury (new)
  'Petty Cash': IconCash,
  'Cash Application': IconCash,
  'Bank Import': IconBuildingBank,
  'Plaid Feeds': IconBuildingBank,
  'Check & Wire': IconCreditCard,
  'Payment Links': IconCurrencyDollar,
  'Payment Orchestrator': IconArrowsExchange,
  'Payment Terms': IconCalendarTime,
  Chargebacks: IconReceiptRefund,
  // Financial Ops (new)
  Intercompany: IconArrowsExchange,
  'Financial Close': IconLock,
  'Lease Accounting': IconBuildingBank,
  'Revenue Recognition': IconReportMoney,
  'Cost Centers': IconChartBar,
  'Consolidated Tax': IconCalculator,
  'Deferred Tax': IconCalculator,
  'Bad Debt': IconAlertTriangle,
  'AP Aging': IconClock,
  'AP Duplicates': IconAlertCircle,
  'Credit Scoring': IconChartLine,
  'Expense Reimburse.': IconReceiptRefund,
  // Tax Forms (new)
  'Form 8300': IconFileCheck,
  'Form 4562': IconFileCheck,
  'Schedule M-1': IconFileCheck,
  // Payroll (new extended)
  'Payroll ACH': IconBuildingBank,
  'Payroll Register': IconFileDescription,
  Garnishments: IconAlertTriangle,
  'Retro Pay': IconCash,
  Tips: IconCash,
  International: IconWorld,
  'State Wages': IconReportMoney,
  'SUTA Filing': IconFileCheck,
  'State Reciprocity': IconArrowsExchange,
  'Certified Payroll': IconCertificate,
  'CSE Remittance': IconFileCheck,
  'New Hire Reports': IconUsers,
  'Earned Wage Access': IconCash,
  'Form 940': IconFileCheck,
  'Form 941': IconFileCheck,
  'Form 943': IconFileCheck,
  'W-2 Management': IconFileDescription,
  'W-4 Tracking': IconFileDescription,
  'Form 1099': IconFileCheck,
  Automation: IconRobot,
  'AI Approval': IconShieldCheck,
  'GL Reconciliation': IconReportAnalytics,
  // TMS (new)
  'TMS Advanced': IconRoute,
  'TMS Intelligence': IconChartLine,
  'Rate Shopping': IconCurrencyDollar,
  'Freight Audit': IconFileAnalytics,
  'Load Planning': IconTruckLoading,
  'Route Accounting': IconReportMoney,
  // Fleet (new extended)
  'Fleet TMS': IconTruckDelivery,
  'Driver Performance': IconChartBar,
  'Drug/Alcohol Testing': IconTestPipe,
  Dashcam: IconGps,
  // Extended Compliance (new)
  Permits: IconCertificate,
  Bioterrorism: IconShield,
  BCP: IconShield,
  CFATS: IconShield,
  'Carbon & ESG': IconLeaf,
  'Commodity Assess.': IconClipboardCheck,
  'Data Breach': IconAlertCircle,
  FCRA: IconFileCheck,
  'FDA FURLS': IconCertificate,
  'FDA Registration': IconCertificate,
  'FSMA Transport': IconTruck,
  'Import Duty': IconWorld,
  'Marketing Compliance': IconFileCheck,
  'OFAC Sanctions': IconShieldLock,
  // Regulatory (new)
  'Organic Enforcement': IconLeaf,
  'PCI Compliance': IconLock,
  Phytosanitary: IconLeaf,
  Privacy: IconLock,
  'State Organic': IconLeaf,
  USERRA: IconShield,
  'WARN Act': IconAlertTriangle,
  'Weights & Measures': IconCalculator,
  // Virginia Tax (new)
  'VA Compliance': IconCalculator,
  'ST-1 Sales Tax': IconFileCheck,
  'VA-4 Certificate': IconFileCheck,
  'VA-5 Filing': IconFileCheck,
  'VA-15 Deposit': IconFileCheck,
  'VA-16 Reconciliation': IconFileCheck,
  'VA Withholding': IconCalculator,
  'Corporate Tax': IconCalculator,
  IFTA: IconTruck,
  'Filing Frequency': IconCalendarTime,
  'Penalty & Interest': IconAlertTriangle,
  'Tangible Property': IconBuildingWarehouse,
  'Tax Intelligence': IconChartLine,
  VDACS: IconCertificate,
  'VDH Health': IconShield,
  // HR (new extended)
  'ACA Compliance': IconShield,
  '401(k)': IconBuildingBank,
  ERISA: IconShield,
  'Leave & Benefits': IconCalendarEvent,
  Onboarding: IconUsers,
  LMS: IconTemplate,
  // AI (new extended)
  'AI Copilot': IconRobot,
  'AI Email Parsing': IconRobot,
  'AI Quality': IconRobot,
  'AI Order Chat': IconRobot,
  'ML Forecasting': IconTrendingUp,
  'Demand Intelligence': IconChartLine,
  'Market Intelligence': IconChartLine,
  'Voice Agent': IconRobot,
  'Voice Agent Live': IconActivity,
  'Voice Prompts': IconFileDescription,
  'Financial Intelligence': IconChartLine,
  'Vendor Cost Intel': IconChartLine,
  'Profit Optimization': IconTrendingUp,
  // Financial Statements
  'Income Statement (P&L)': IconFileAnalytics,
  'Balance Sheet': IconBuildingBank,
  'Cash Flow Statement': IconCash,
  'Reports Builder': IconChartBar,
  'Financial Dashboard': IconChartLine,
  // Reports (new)
  'BI Dashboard': IconLayoutDashboard,
  'Report Builder': IconChartBar,
  'Async Reports': IconFileAnalytics,
  // Admin (new extended)
  SSO: IconLock,
  OAuth: IconLock,
  'API Gateway': IconWorld,
  Notifications: IconBell,
  'Key Rotation': IconLock,
  Archival: IconDatabase,
  'Data Migration': IconDatabase,
  'GP Migration': IconDatabase,
  'Search Config': IconSearch,
  'Health Checks': IconActivity,
  DocuSign: IconFileCheck,
  'Slack Webhooks': IconWorld,
  // Manufacturing
  'Work Orders': IconTool,
  'Bill of Materials': IconListCheck,
  'Work Centers': IconBuildingWarehouse,
  'Production Tracking': IconActivity,
  'Change Orders (ECO)': IconGitBranch,
  // Service Desk
  'Support Tickets': IconUsers,
  'Agent Desktop': IconUsers,
  'Knowledge Base': IconFileDescription,
  // Automation
  'Automation Hub': IconPlayerPlay,
  'Workflow Rules': IconPlayerPlay,
  'Workflow Monitor': IconActivity,
  'Recurring Tasks': IconRefresh,
  // Compliance Scheduler
  'Compliance Scheduler': IconCalendarEvent,
  // T&A (new)
  'Shift Swaps': IconArrowsExchange,
  'PTO Policies': IconFileCheck,
  'Team Calendar': IconCalendarEvent,
  'AI Intelligence': IconRobot,
  // Kiosk
  'Kiosk Terminals': IconSettings,
  // Maintenance
  'Equipment & Work Orders': IconTool,
};

const NAV_PERMISSION_MAP: Record<string, string> = {
  '/sales': 'sales.view',
  '/crm': 'crm.view',
  '/purchasing': 'purchasing.view',
  '/pricing': 'pricing.view',
  '/commerce': 'sales.view',
  '/inventory': 'inventory.view',
  '/iot': 'inventory.view',
  '/operations': 'operations.view',
  '/warehouse': 'warehouse.view',
  '/supply-chain': 'inventory.view',
  '/maintenance': 'maintenance.view',
  '/manufacturing': 'manufacturing.view',
  '/accounting': 'accounting.view',
  '/payroll': 'payroll.view',
  '/ta': 'payroll.view',
  '/kiosk': 'payroll.view',
  '/finance': 'finance.view',
  '/ap': 'finance.view',
  '/forecasting': 'finance.view',
  '/fleet': 'fleet.view',
  '/tms': 'fleet.view',
  '/logistics': 'fleet.view',
  '/compliance': 'compliance.view',
  '/regulatory': 'compliance.view',
  '/audit': 'compliance.view',
  '/va-tax': 'compliance.view',
  '/hr': 'hr.view',
  '/service': 'support.view',
  '/automation': 'automation.view',
  '/workflow': 'automation.view',
  '/integrations': 'automation.view',
  '/ai': 'ai.view',
  '/reports': 'reports.view',
  '/admin': 'admin.view',
};

const SECTION_TABS: { key: string; icon: typeof IconDashboard; label: string }[] = [
  { key: 'MY WORKSPACE', icon: IconHome, label: 'My Workspace' },
  { key: 'SALES & CUSTOMERS', icon: IconShoppingCart, label: 'Sales' },
  { key: 'PURCHASING & VENDORS', icon: IconFileInvoice, label: 'Purchasing' },
  { key: 'WAREHOUSE & INVENTORY', icon: IconBuildingWarehouse, label: 'Warehouse' },
  { key: 'ACCOUNTING & FINANCE', icon: IconReportMoney, label: 'Accounting' },
  { key: 'FINANCIAL REPORTS', icon: IconFileAnalytics, label: 'Reports' },
  { key: 'PAYROLL & HR', icon: IconCash, label: 'Payroll & HR' },
  { key: 'FLEET & LOGISTICS', icon: IconTruckDelivery, label: 'Fleet' },
  { key: 'COMPLIANCE & FOOD SAFETY', icon: IconShield, label: 'Compliance' },
  { key: 'DOCUMENTS & ADMIN', icon: IconFolders, label: 'Admin' },
];

// ---------------------------------------------------------------------------
// Role-based navigation: show only relevant sections per role.
// Users with 'admin' role see everything. Others see a focused task-oriented
// subset. Users can toggle "Show All Modules" to access the full nav.
// ---------------------------------------------------------------------------
const ROLE_SECTIONS: Record<string, string[]> = {
  // Sales rep: orders, customers, CRM — focused on selling
  sales_rep: ['MY WORKSPACE', 'SALES & CUSTOMERS'],
  // Warehouse staff: inventory, picking, shipping — floor operations
  warehouse: ['MY WORKSPACE', 'WAREHOUSE & INVENTORY'],
  picker: ['MY WORKSPACE', 'WAREHOUSE & INVENTORY'],
  receiver: ['MY WORKSPACE', 'WAREHOUSE & INVENTORY'],
  // Driver: fleet only
  driver: ['MY WORKSPACE', 'FLEET & LOGISTICS'],
  // Accountant: GL, AP, AR, banking, reports
  accountant: ['MY WORKSPACE', 'SALES & CUSTOMERS', 'PURCHASING & VENDORS', 'ACCOUNTING & FINANCE', 'FINANCIAL REPORTS'],
  controller: ['MY WORKSPACE', 'SALES & CUSTOMERS', 'PURCHASING & VENDORS', 'ACCOUNTING & FINANCE', 'FINANCIAL REPORTS', 'COMPLIANCE & FOOD SAFETY', 'DOCUMENTS & ADMIN'],
  // Payroll/HR
  payroll_admin: ['MY WORKSPACE', 'PAYROLL & HR'],
  hr_manager: ['MY WORKSPACE', 'PAYROLL & HR'],
  // Purchasing
  purchasing_manager: ['MY WORKSPACE', 'PURCHASING & VENDORS', 'WAREHOUSE & INVENTORY'],
  // Compliance officer
  compliance_officer: ['MY WORKSPACE', 'COMPLIANCE & FOOD SAFETY', 'WAREHOUSE & INVENTORY'],
  // Order taker: sales only, simplified
  order_taker: ['MY WORKSPACE', 'SALES & CUSTOMERS'],
  // Manager: their department + reports
  manager: ['MY WORKSPACE', 'SALES & CUSTOMERS', 'PURCHASING & VENDORS', 'WAREHOUSE & INVENTORY', 'FINANCIAL REPORTS', 'PAYROLL & HR'],
  // Viewer: everything read-only (nav is the same as admin but permissions block writes)
  viewer: ['MY WORKSPACE', 'SALES & CUSTOMERS', 'PURCHASING & VENDORS', 'WAREHOUSE & INVENTORY', 'ACCOUNTING & FINANCE', 'FINANCIAL REPORTS', 'PAYROLL & HR'],
};

function getSectionsForRoles(roles: string[]): string[] | null {
  // Admin sees everything — return null to skip filtering
  if (roles.includes('admin')) return null;
  // Merge sections from all assigned roles
  const sections = new Set<string>();
  for (const role of roles) {
    const roleSections = ROLE_SECTIONS[role];
    if (roleSections) {
      roleSections.forEach((s) => sections.add(s));
    }
  }
  // If no role mapping found, show everything (fail-open for unknown roles)
  return sections.size > 0 ? Array.from(sections) : null;
}

/** Strip query string from a path for comparison purposes. */
const basePath = (p: string) => p.split('?')[0];

function detectSectionFromPath(pathname: string): string {
  for (const section of navSections) {
    for (const item of section.items) {
      if (item.path && pathname === basePath(item.path)) return section.title;
      if (item.children?.some((c) => pathname.startsWith(basePath(c.path)))) return section.title;
    }
  }
  return 'MY WORKSPACE';
}

const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 280;

export default function Layout() {
  const [opened, { toggle, close }] = useDisclosure();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [searchOpened, { open: openSearch, close: closeSearch }] = useDisclosure(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { user, logout, hasPermission } = useAuthStore();
  const [navFilter, setNavFilter] = useState('');
  const [activeSection, setActiveSection] = useState<string>(() => {
    try {
      let saved = localStorage.getItem('sidebar-section');
      // Migrate legacy section names from prior layouts
      if (saved === 'COMMERCE' || saved === 'MAIN') saved = 'MY WORKSPACE';
      if (saved === 'SALES') saved = 'SALES & CUSTOMERS';
      if (saved === 'PURCHASING') saved = 'PURCHASING & VENDORS';
      if (saved === 'WAREHOUSE') saved = 'WAREHOUSE & INVENTORY';
      if (saved === 'FINANCE') saved = 'ACCOUNTING & FINANCE';
      if (saved === 'PAYROLL' || saved === 'HR') saved = 'PAYROLL & HR';
      if (saved === 'COMPLIANCE' || saved === 'TAX') saved = 'COMPLIANCE & FOOD SAFETY';
      if (saved === 'SYSTEM') saved = 'DOCUMENTS & ADMIN';
      return saved && SECTION_TABS.some((t) => t.key === saved)
        ? saved
        : detectSectionFromPath(location.pathname);
    } catch {
      return detectSectionFromPath(location.pathname);
    }
  });
  const { suggestions, mode: anticipationMode } = useTaskAnticipation();

  const canAccess = (path: string): boolean => {
    if (user?.role === 'admin') return true;
    // Dashboard is accessible to all authenticated users
    if (path === '/') return true;
    for (const [prefix, perm] of Object.entries(NAV_PERMISSION_MAP)) {
      if (path.startsWith(prefix)) return hasPermission(perm);
    }
    // Deny by default — unmapped routes require explicit permission mapping
    return false;
  };

  const filteredSections = useMemo(() => {
    const q = navFilter.trim().toLowerCase();

    return navSections
      .map((section) => ({
        ...section,
        items: section.items
          .map((item) => {
            // Role-based filtering
            if (item.requiredPermission && !hasPermission(item.requiredPermission)) return null;
            if (item.path && !canAccess(item.path)) return null;

            // Text search filtering
            if (q) {
              if (item.label.toLowerCase().includes(q)) {
                // Parent matches — show all accessible children
                if (!item.children) return item;
                const accessible = item.children.filter((c) => canAccess(c.path));
                return accessible.length > 0 ? { ...item, children: accessible } : null;
              }
              if (!item.children) return null;
              const matched = item.children.filter(
                (c) => c.label.toLowerCase().includes(q) && canAccess(c.path),
              );
              return matched.length > 0 ? { ...item, children: matched } : null;
            }

            // No text search — just role filter children
            if (!item.children) return item;
            const accessible = item.children.filter((c) => canAccess(c.path));
            return accessible.length > 0 ? { ...item, children: accessible } : null;
          })
          .filter(Boolean) as NavItem[],
      }))
      .filter((s) => s.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navFilter, user?.role, user?.permissions]);

  const displayedSections = useMemo(() => {
    if (navFilter.trim()) return filteredSections;
    return filteredSections.filter((s) => s.title === activeSection);
  }, [filteredSections, navFilter, activeSection]);

  // Role-based section filtering: show only relevant modules for the user's role
  const [showAllModules, setShowAllModules] = useState(() => {
    try { return localStorage.getItem('show-all-modules') === '1'; } catch { return false; }
  });

  // Only show section tabs that are relevant to this user's role
  const visibleTabs = useMemo(() => {
    const accessibleTitles = new Set(filteredSections.map((s) => s.title));
    const permissionFiltered = SECTION_TABS.filter((tab) => accessibleTitles.has(tab.key));

    // Admin or "Show All" mode: show everything accessible
    if (user?.role === 'admin' || showAllModules) return permissionFiltered;

    // Role-based filtering: only show sections mapped to the user's roles
    const userRoles = [user?.role, ...(user?.roles || [])].filter(Boolean) as string[];
    const roleSections = getSectionsForRoles(userRoles);
    if (!roleSections) return permissionFiltered; // Unknown role → show all
    return permissionFiltered.filter((tab) => roleSections.includes(tab.key));
  }, [filteredSections, user?.role, user?.roles, showAllModules]);

  useEffect(() => {
    try {
      localStorage.setItem('sidebar-collapsed', String(collapsed));
    } catch { /* noop */ }
  }, [collapsed]);

  useEffect(() => {
    const detected = detectSectionFromPath(location.pathname);
    // Ensure detected section is visible for this user's role
    const accessibleTitles = new Set(filteredSections.map((s) => s.title));
    if (accessibleTitles.has(detected)) {
      setActiveSection(detected);
    } else if (accessibleTitles.size > 0) {
      setActiveSection(filteredSections[0].title);
    }
  }, [location.pathname, filteredSections]);

  useEffect(() => {
    try {
      localStorage.setItem('sidebar-section', activeSection);
    } catch { /* noop */ }
  }, [activeSection]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  const handleNavigate = (path: string) => {
    navigate(path);
    close();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const sidebarWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{
        width: sidebarWidth,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <SkipLink />
      <RouteAnnouncer pathname={location.pathname} appName="Standard Produce ERP" />
      {/* ---- Header ---- */}
      <AppShell.Header style={{ boxShadow: 'var(--mantine-shadow-sm)', border: 'none' }}>
        <Group h="100%" px="lg" justify="space-between">
          <Group gap="sm">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation menu"
            />
            <Group
              gap={8}
              style={{ cursor: 'pointer' }}
              onClick={() => handleNavigate('/dashboard')}
            >
              <img
                src="/logo.png"
                alt="Standard Produce Co."
                style={{
                  height: 56,
                  filter: colorScheme === 'dark' ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </Group>
          </Group>

          <Group gap="xs" visibleFrom="sm" style={{ flex: 1, maxWidth: 420, marginLeft: 24 }}>
            <Box
              onClick={openSearch}
              role="button"
              tabIndex={0}
              aria-label="Open search (Ctrl+K)"
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openSearch();
                }
              }}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 'var(--mantine-radius-sm)',
                backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
                cursor: 'pointer',
                transition: 'background-color 150ms ease',
              }}
            >
              <IconSearch size={16} style={{ opacity: 0.5 }} />
              <Text size="sm" c="dimmed" style={{ flex: 1 }}>Search...</Text>
              <Kbd size="xs" style={{ fontSize: 10, padding: '2px 6px' }}>
                Ctrl+K
              </Kbd>
            </Box>
          </Group>

          <Group gap="xs">
            <Tooltip label="Toggle theme">
              <ActionIcon
                variant="subtle"
                size="lg"
                radius="xl"
                onClick={() => toggleColorScheme()}
                aria-label={`Switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {colorScheme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Notifications">
              <ActionIcon
                variant="subtle"
                size="lg"
                radius="xl"
                aria-label="Notifications"
              >
                <IconBell size={20} />
              </ActionIcon>
            </Tooltip>

            <Menu shadow="lg" width={220} position="bottom-end" radius="md">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  radius="xl"
                  aria-label={`User menu for ${user?.display_name || 'User'}`}
                >
                  <Avatar size="sm" color="brand" variant="filled" radius="xl">
                    {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="sm" fw={500}>{user?.display_name || 'User'}</Text>
                  <Badge size="xs" variant="light" mt={4}>
                    {user?.role || 'Unknown role'}
                  </Badge>
                </Menu.Label>
                <Menu.Divider />
                <Menu.Item leftSection={<IconUserCircle size={16} />}>
                  Profile
                </Menu.Item>
                <Menu.Item leftSection={<IconSettings size={16} />}>
                  Settings
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* ---- Sidebar ---- */}
      <AppShell.Navbar
        p="xs"
        aria-label="Sidebar navigation"
        style={{
          border: 'none',
          boxShadow: '1px 0 0 light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))',
          transition: 'width 200ms ease',
        }}
      >
        {!collapsed && (
          <AppShell.Section px="xs" pt={4} pb={4}>
            <TextInput
              placeholder="Filter nav..."
              size="xs"
              leftSection={<IconSearch size={14} />}
              rightSection={
                navFilter ? (
                  <CloseButton size="xs" onClick={() => setNavFilter('')} aria-label="Clear filter" />
                ) : null
              }
              value={navFilter}
              onChange={(e) => setNavFilter(e.currentTarget.value)}
              aria-label="Filter navigation items"
            />
          </AppShell.Section>
        )}
        {/* Dashboard — always visible */}
        <AppShell.Section px="xs" pb={2}>
          {collapsed ? (
            <Tooltip label="Dashboard" position="right">
              <NavLink
                label=""
                leftSection={<IconDashboard size={20} />}
                active={location.pathname === '/'}
                aria-current={location.pathname === '/' ? 'page' : undefined}
                onClick={() => handleNavigate('/')}
                variant="light"
                styles={{
                  root: {
                    justifyContent: 'center',
                    paddingLeft: 0,
                    paddingRight: 0,
                    borderLeft: location.pathname === '/'
                      ? '3px solid var(--mantine-color-brand-5)'
                      : '3px solid transparent',
                  },
                  section: { marginRight: 0 },
                }}
              />
            </Tooltip>
          ) : (
            <NavLink
              label="Dashboard"
              leftSection={<IconDashboard size={20} />}
              active={location.pathname === '/'}
              aria-current={location.pathname === '/' ? 'page' : undefined}
              onClick={() => handleNavigate('/')}
              variant="light"
              styles={{
                root: {
                  borderLeft: location.pathname === '/'
                    ? '3px solid var(--mantine-color-brand-5)'
                    : '3px solid transparent',
                },
              }}
            />
          )}
        </AppShell.Section>

        {!collapsed && (
          <AppShell.Section px="xs" pb={4}>
            <Group gap={4} justify="center" wrap="wrap">
              {visibleTabs.map((tab) => {
                const TabIcon = tab.icon;
                const isTabActive = activeSection === tab.key;
                return (
                  <Tooltip key={tab.key} label={tab.label} position="bottom">
                    <ActionIcon
                      variant={isTabActive ? 'filled' : 'subtle'}
                      color={isTabActive ? 'brand' : 'gray'}
                      size="lg"
                      onClick={() => {
                        setActiveSection(tab.key);
                        setNavFilter('');
                      }}
                      aria-label={tab.label}
                    >
                      <TabIcon size={20} />
                    </ActionIcon>
                  </Tooltip>
                );
              })}
            </Group>
            {user?.role !== 'admin' && (
              <Text
                size="xs"
                c="dimmed"
                ta="center"
                mt={4}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const next = !showAllModules;
                  setShowAllModules(next);
                  try { localStorage.setItem('show-all-modules', next ? '1' : '0'); } catch {}
                }}
              >
                {showAllModules ? '◉ Focused View' : '○ Show All Modules'}
              </Text>
            )}
          </AppShell.Section>
        )}
        <AppShell.Section grow component={ScrollArea} scrollbarSize={4}>
          {displayedSections.map((section) => {
            // Group items by subsection for subheader rendering
            const subsectionGroups: { key: string; items: NavItem[] }[] = [];
            const seen = new Set<string>();
            for (const item of section.items) {
              const sub = item.subsection || '';
              if (!seen.has(sub)) {
                seen.add(sub);
                subsectionGroups.push({ key: sub, items: [] });
              }
              subsectionGroups.find((g) => g.key === sub)!.items.push(item);
            }

            return (
            <Box key={section.title} mb="xs">
              {!collapsed && (
                <Text
                  size="xs"
                  fw={700}
                  c="dimmed"
                  tt="uppercase"
                  px="sm"
                  py={6}
                  style={{ letterSpacing: '0.05em', fontSize: 10 }}
                >
                  {section.title}
                </Text>
              )}
              {subsectionGroups.map((group) => (
                <Box key={group.key || '_none'}>
                  {!collapsed && group.key && (
                    <Text
                      size="xs"
                      fw={600}
                      c="dimmed"
                      px="sm"
                      pt={8}
                      pb={2}
                      style={{ fontSize: 10, opacity: 0.7 }}
                    >
                      {group.key}
                    </Text>
                  )}
                  {group.items.map((item) => {
                const Icon = item.icon;

                if (item.path) {
                  const active = location.pathname === item.path;
                  return collapsed ? (
                    <Tooltip key={item.label} label={item.label} position="right">
                      <NavLink
                        label=""
                        leftSection={<Icon size={20} />}
                        active={active}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => handleNavigate(item.path!)}
                        variant="light"
                        styles={{
                          root: {
                            justifyContent: 'center',
                            paddingLeft: 0,
                            paddingRight: 0,
                            borderLeft: active
                              ? '3px solid var(--mantine-color-brand-5)'
                              : '3px solid transparent',
                          },
                          section: { marginRight: 0 },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <NavLink
                      key={item.label}
                      label={item.label}
                      leftSection={<Icon size={20} />}
                      active={active}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => handleNavigate(item.path!)}
                      variant="light"
                      styles={{
                        root: {
                          borderLeft: active
                            ? '3px solid var(--mantine-color-brand-5)'
                            : '3px solid transparent',
                        },
                      }}
                    />
                  );
                }

                const isActive = item.children?.some((c) =>
                  location.pathname.startsWith(basePath(c.path)),
                );

                if (collapsed) {
                  return (
                    <Menu key={item.label} shadow="lg" position="right-start" width={200}>
                      <Menu.Target>
                        <Tooltip label={item.label} position="right">
                          <NavLink
                            label=""
                            leftSection={<Icon size={20} />}
                            active={isActive}
                            variant="light"
                            styles={{
                              root: {
                                justifyContent: 'center',
                                paddingLeft: 0,
                                paddingRight: 0,
                                borderLeft: isActive
                                  ? '3px solid var(--mantine-color-brand-5)'
                                  : '3px solid transparent',
                              },
                              section: { marginRight: 0 },
                            }}
                          />
                        </Tooltip>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Label>{item.label}</Menu.Label>
                        {item.children?.map((child) => (
                          <Menu.Item
                            key={child.path}
                            onClick={() => handleNavigate(child.path)}
                            fw={location.pathname + location.search === child.path ? 600 : 400}
                            c={location.pathname + location.search === child.path ? 'brand' : undefined}
                          >
                            {child.label}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  );
                }

                return (
                  <NavLink
                    key={item.label}
                    label={item.label}
                    leftSection={<Icon size={20} />}
                    childrenOffset={28}
                    defaultOpened={!!isActive || !!navFilter.trim()}
                    rightSection={<IconChevronRight size={14} />}
                    variant="light"
                    styles={{
                      root: {
                        borderLeft: isActive
                          ? '3px solid var(--mantine-color-brand-5)'
                          : '3px solid transparent',
                      },
                    }}
                  >
                    {item.children?.map((child) => {
                      const ChildIcon = iconMap[child.label] || IconChevronRight;
                      const childActive = location.pathname + location.search === child.path;
                      return (
                        <NavLink
                          key={child.path}
                          label={child.label}
                          leftSection={<ChildIcon size={16} />}
                          active={childActive}
                          aria-current={childActive ? 'page' : undefined}
                          onClick={() => handleNavigate(child.path)}
                          variant="light"
                        />
                      );
                    })}
                  </NavLink>
                );
              })}
                </Box>
              ))}
            </Box>
          );
          })}
        </AppShell.Section>

        {/* Collapse toggle + user */}
        <AppShell.Section>
          <Divider mb="xs" />
          {collapsed ? (
            <Box style={{ textAlign: 'center' }} py="xs">
              <Tooltip label="Expand sidebar" position="right">
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={() => setCollapsed(false)}
                  aria-label="Expand sidebar"
                >
                  <IconChevronRight size={16} />
                </ActionIcon>
              </Tooltip>
            </Box>
          ) : (
            <>
              <Group p="xs" gap="xs" wrap="nowrap">
                <Avatar size="sm" color="brand" variant="filled" radius="xl">
                  {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {user?.display_name || 'User'}
                  </Text>
                  <Badge size="xs" variant="light">
                    {user?.role || ''}
                  </Badge>
                </div>
                <Tooltip label="Collapse sidebar">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => setCollapsed(true)}
                    aria-label="Collapse sidebar"
                  >
                    <IconChevronLeft size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </>
          )}
        </AppShell.Section>
      </AppShell.Navbar>

      {/* ---- Main Content ---- */}
      <AppShell.Main id="main-content" role="main" tabIndex={-1}>
        <NextTaskSuggestion suggestions={suggestions} mode={anticipationMode} />
        <Outlet />
      </AppShell.Main>

      <SearchSpotlight opened={searchOpened} onClose={closeSearch} />
    </AppShell>
  );
}
